import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../../Navbar';
import { useAuth } from '../../../context/AuthContext';
import { useUser } from '../../../context/UserContext';
import { staffService } from '../../../services';
import { SECTIONS, SECTION_BY_ID, railGroups, sectionCount } from '../../../utils/staffCapabilities';
import { AdminShellContext } from './AdminShellContext';
import PersonDrawer from '../PersonDrawer';
import useArea from './useArea';
import { Avatar, Btn, DarkCard, MAGENTA, Toast, ringBorder } from './ui';

const LAST_SEEN_KEY = 'pr.admin.lastSeen';
const RAIL_KEY = 'pr.admin.railCollapsed';

function readJson(key, fallback) { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; } }
function writeJson(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ } }

const SearchGlyph = ({ size = 14, stroke = '#000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" className="flex-shrink-0"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
);

/**
 * One shell for every staff section. Measures its own width (not the window):
 * ≥1180 desktop (a 276px rail in a white radius-28 panel, 92px collapsed and the
 * collapse remembered), 720–1179 tablet (forced collapsed), <720 phone (a black pill
 * section bar over a dark sheet). The rail is built from the capability registry and
 * filtered by the signed-in person's key; the page itself is an <Outlet/>.
 */
export default function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { profile, walletBalance, staff, staffLoaded, isStaff, previewStaff, previewAs, can } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const rootRef = useRef(null);
  const goToRef = useRef(null);
  const [mode, setMode] = useState('desktop');
  const [collapsedPref, setCollapsedPref] = useState(() => readJson(RAIL_KEY, false));
  const [sheetOpen, setSheetOpen] = useState(false);
  const [goTo, setGoTo] = useState('');
  const [counts, setCounts] = useState(null);
  const [lastSeen, setLastSeen] = useState(() => readJson(LAST_SEEN_KEY, {}));
  const [takeover, setTakeover] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const toastTimer = useRef(null);
  const area = useArea();

  // Measure the container, not the viewport. A callback ref, because the shell root only
  // exists once the key has loaded — an effect on mount would observe nothing.
  const observerRef = useRef(null);
  const measureRef = useCallback((el) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    rootRef.current = el;
    if (!el) return;
    const apply = (w) => setMode(w < 720 ? 'mobile' : w < 1180 ? 'tablet' : 'desktop');
    apply(el.getBoundingClientRect().width || window.innerWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => apply(Math.round(entries[0].contentRect.width)));
    ro.observe(el);
    observerRef.current = ro;
  }, []);
  useEffect(() => () => observerRef.current?.disconnect(), []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const isMobile = mode === 'mobile';
  const isTablet = mode === 'tablet';
  const expanded = !collapsedPref && !isTablet;

  const toast = useCallback((message) => {
    setToastMsg(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2600);
  }, []);

  const refreshCounts = useCallback(async () => {
    const r = await staffService.counts();
    if (r?.success) setCounts(r.data);
  }, []);
  useEffect(() => {
    if (!isStaff) return undefined;
    refreshCounts();
    const t = setInterval(refreshCounts, 60_000);
    return () => clearInterval(t);
  }, [isStaff, refreshCounts]);

  // `/admin-preview/...` is the dev-only fixture harness; it mounts the same shell, so
  // every link inside stays on whichever base the shell was opened under.
  const base = location.pathname.startsWith('/admin-preview') ? '/admin-preview' : '/admin';
  const segments = location.pathname.slice(base.length).split('/').filter(Boolean);
  const sectionId = segments[0] || 'today';
  const section = SECTION_BY_ID[sectionId];
  const groups = useMemo(() => railGroups(staff), [staff]);
  const visibleIds = useMemo(() => new Set(groups.flatMap((g) => g.sections.map((s) => s.id))), [groups]);

  // Opening a section clears its activity dot.
  useEffect(() => {
    if (!section) return;
    setLastSeen((prev) => { const next = { ...prev, [section.id]: Date.now() }; writeJson(LAST_SEEN_KEY, next); return next; });
    setSheetOpen(false);
  }, [section]);

  // `/` focuses GO TO while the panel is open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setSheetOpen(false); return; }
      if (e.key !== '/' || e.metaKey || e.ctrlKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      if (goToRef.current) { e.preventDefault(); goToRef.current.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleCollapsed = () => setCollapsedPref((c) => { writeJson(RAIL_KEY, !c); return !c; });

  const dotFor = (s) => {
    if (!s.dot || !counts) return false;
    const seen = lastSeen[s.id] || 0;
    if (s.id === 'audit') return (counts.criticalActivity24h || 0) > 0 && Date.now() - seen > 3600_000;
    if (s.id === 'newsletter') return (counts.newsletterDrafts || 0) > 0 && Date.now() - seen > 86400_000;
    return false;
  };
  const badgeFor = (s) => (s.badge && counts ? counts[s.badge] : null);

  const goToMatches = useMemo(() => {
    const q = goTo.trim().toLowerCase();
    if (!q) return [];
    return SECTIONS.filter((s) => visibleIds.has(s.id) && (s.label.toLowerCase().includes(q) || s.id.includes(q))).slice(0, 6);
  }, [goTo, visibleIds]);
  const submitGoTo = (e) => {
    e.preventDefault();
    const q = goTo.trim();
    if (!q) return;
    if (q.includes('@') && visibleIds.has('users')) navigate(`${base}/users?search=${encodeURIComponent(q)}`);
    else if (goToMatches[0]) navigate(`${base}/${goToMatches[0].id}`);
    setGoTo('');
    goToRef.current?.blur();
  };

  // The person drawer is one component mounted once, addressed by the URL. Any section
  // opens it with openPerson(id) — nothing renders a second copy, and a drawer that is
  // open survives a reload and can be linked to.
  const personId = searchParams.get('person');
  const canSeePeople = can('users', 'view');
  const openPerson = useCallback((id) => {
    if (!id) return;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('person', String(id)); return next; });
  }, [setSearchParams]);
  const closePerson = useCallback(() => {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('person'); return next; }, { replace: true });
  }, [setSearchParams]);

  const ctx = useMemo(
    () => ({ mode, isMobile, isTablet, counts, refreshCounts, area, setTakeover, lastSeen, toast, openPerson, closePerson, canSeePeople }),
    [mode, isMobile, isTablet, counts, refreshCounts, area, lastSeen, toast, openPerson, closePerson, canSeePeople],
  );

  // PREVIEWING AS … — a pink pill above everything, the only way out of a preview.
  const previewBanner = previewStaff && (
    <div className="sticky top-0 z-[45] px-4 pt-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-full px-5 py-[10px]" style={{ background: MAGENTA, color: '#fff' }}>
        <span className="font-mono text-[11px] font-extrabold uppercase tracking-[0.14em]">PREVIEWING AS {(previewStaff.name || previewStaff.email || '').toUpperCase()}</span>
        <span className="min-w-0 flex-1 font-mono text-[10px] uppercase tracking-[0.12em] text-white/80">
          {previewStaff.status === 'suspended'
            ? 'SUSPENDED · EVERY CHECK FAILS'
            : `${previewStaff.presetWord || 'CUSTOM'} · ${sectionCount(previewStaff)} SECTIONS · ${previewStaff.verbCount ?? Object.values(previewStaff.grants || {}).reduce((n, v) => n + v.length, 0)} VERBS`}
        </span>
        <button
          type="button"
          onClick={() => { const id = previewStaff.userId; previewAs(null); if (id) navigate(`/admin/staff/${id}`); }}
          className="h-9 flex-shrink-0 rounded-full bg-white px-4 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-black hover:opacity-85"
        >
          EXIT PREVIEW
        </button>
      </div>
    </div>
  );

  const navbar = (
    <Navbar
      user={profile}
      walletBalance={walletBalance}
      onLogout={logout}
      onLogoClick={() => navigate('/featured')}
      variant="admin"
    />
  );

  if (!staffLoaded) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        {navbar}
        <div className="p-8 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">LOADING…</div>
      </div>
    );
  }

  const lockCard = (title, body, cta) => (
    <div className="flex justify-center px-4 py-14">
      <DarkCard className="flex max-w-[460px] flex-col gap-4 p-8 text-center">
        <div className="whitespace-nowrap font-mono text-[20px] font-bold uppercase tracking-[0.04em]">{title}</div>
        <div className="font-mono text-[12px] leading-[1.6] text-[#BDBDBD]">{body}</div>
        {cta}
      </DarkCard>
    </div>
  );

  // Signed in, not staff: no shell, the normal navbar, one card, one way out.
  if (!isStaff) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <Navbar user={profile} walletBalance={walletBalance} onLogout={logout} onLogoClick={() => navigate('/featured')} />
        {previewBanner}
        {lockCard(
          "THIS DOOR ISN'T YOURS.",
          'NOTHING TO SEE, NOTHING BROKEN. YOUR PARTIES ARE THIS WAY.',
          <Btn variant="white" className="self-center" onClick={() => navigate('/featured')}>FIND YOUR PARTY</Btn>,
        )}
      </div>
    );
  }

  const denied = !section || !visibleIds.has(section.id);
  const firstSection = groups[0]?.sections[0];
  const displayName = staff?.displayName || profile?.displayName || '';
  const presetLine = `${staff?.presetWord || 'CUSTOM'} · ${sectionCount(staff)} SECTIONS`;

  const markFor = (s, active, size = 30) => (
    <span
      className="flex flex-shrink-0 items-center justify-center rounded-full font-mono text-[8px] font-extrabold tracking-normal"
      style={active
        ? { width: size, height: size, boxSizing: 'border-box', color: '#fff', ...ringBorder('#000') }
        : { width: size, height: size, background: '#F2F2F2', color: '#4B5563' }}
    >
      {s.mark}
    </span>
  );

  const railItem = (s, variant) => {
    const active = s.id === sectionId;
    const badge = badgeFor(s);
    const dot = badge == null && dotFor(s);
    const go = () => { navigate(`${base}/${s.id}`); setSheetOpen(false); };
    if (variant === 'sheet') {
      return (
        <button key={s.id} type="button" onClick={go} className={`flex h-[50px] w-full items-center gap-3 rounded-full py-0 pl-[6px] pr-[14px] text-left font-mono text-[13px] uppercase tracking-[0.08em] text-white ${active ? 'bg-[#1F1F1F] font-bold' : 'font-medium'}`}>
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full font-mono text-[9px] font-bold text-white" style={active ? ringBorder('#0A0A0A') : { background: '#1F1F1F' }}>{s.mark}</span>
          <span className="min-w-0 flex-1 truncate">{s.label}</span>
          {badge != null && badge > 0 && <span className="flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-[7px] font-mono text-[10px] font-bold text-white" style={{ background: MAGENTA }}>{badge > 99 ? '99+' : badge}</span>}
          {badge == null && dot && <span className="h-2 w-2 rounded-full" style={{ background: MAGENTA }} />}
        </button>
      );
    }
    const collapsed = variant === 'collapsed';
    return (
      <button
        key={s.id}
        type="button"
        title={s.label}
        onClick={go}
        className={`relative mb-[2px] flex h-10 w-full items-center gap-[10px] rounded-full font-mono text-[11px] uppercase tracking-[0.1em] transition-colors duration-200 ${collapsed ? 'justify-center px-0' : 'py-0 pl-[5px] pr-[14px] text-left'} ${active ? 'bg-black font-bold text-white' : 'font-medium text-black hover:bg-[#F2F2F2]'}`}
      >
        {markFor(s, active)}
        {!collapsed && <span className="min-w-0 flex-1 truncate">{s.label}</span>}
        {badge != null && badge > 0 && (
          <span
            className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-[6px] font-mono text-[9px] font-bold tracking-normal text-white ${collapsed ? 'absolute right-[2px] top-0' : ''}`}
            style={{ background: MAGENTA, border: collapsed ? '2px solid #fff' : undefined }}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        {badge == null && dot && <span className={`h-2 w-2 flex-shrink-0 rounded-full ${collapsed ? 'absolute right-2 top-[6px]' : ''}`} style={{ background: MAGENTA }} />}
      </button>
    );
  };

  const goToField = (variant) => (
    <form onSubmit={submitGoTo} className="relative">
      <div className={`flex items-center gap-[10px] rounded-full ${variant === 'sheet' ? 'h-[46px] bg-[#1F1F1F] px-4' : 'h-[42px] bg-[#F2F2F2] pl-4 pr-2'}`}>
        <SearchGlyph stroke={variant === 'sheet' ? '#fff' : '#000'} />
        <input
          ref={variant === 'rail' ? goToRef : undefined}
          value={goTo}
          onChange={(e) => setGoTo(e.target.value)}
          placeholder="GO TO…"
          aria-label="Go to a section or a user by email"
          className={`min-w-0 flex-1 border-0 bg-transparent p-0 font-mono uppercase tracking-[0.12em] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0 ${variant === 'sheet' ? 'text-[12px] text-white' : 'text-[11px]'}`}
        />
        {variant === 'rail' && <kbd className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-white font-mono text-[10px] font-bold text-[#9CA3AF]">/</kbd>}
      </div>
      {goToMatches.length > 0 && (
        <div className={`absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-[22px] p-2 ${variant === 'sheet' ? 'bg-[#1F1F1F]' : 'bg-[#0A0A0A]'}`}>
          {goToMatches.map((s) => (
            <button key={s.id} type="button" onMouseDown={() => { navigate(`${base}/${s.id}`); setGoTo(''); setSheetOpen(false); }} className="flex min-h-[42px] w-full items-center gap-[10px] rounded-[14px] px-[14px] text-left font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-white hover:bg-[#262626]">
              <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#262626] text-[8px]">{s.mark}</span>{s.label}
            </button>
          ))}
        </div>
      )}
    </form>
  );

  const footer = (variant) => (
    <div className={`mx-[10px] mb-[10px] flex items-center gap-[10px] rounded-full bg-[#F5F5F5] p-[6px] ${variant === 'collapsed' ? 'justify-center' : ''}`}>
      <Avatar url={profile?.avatarUrl} initials={staff?.initials} name={displayName} size={36} />
      {variant !== 'collapsed' && (
        <div className="flex min-w-0 flex-col gap-[2px] pr-2">
          <span className="truncate font-mono text-[11px] font-bold uppercase tracking-[0.08em]">{displayName}</span>
          <span className="whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.12em]" style={{ color: MAGENTA }}>{presetLine}</span>
        </div>
      )}
    </div>
  );

  const rail = !isMobile && !takeover && (
    <aside className="sticky top-0 box-border h-screen flex-shrink-0 py-4 pl-4 pr-0" style={{ width: expanded ? 276 : 92 }}>
      <div className="flex h-full flex-col overflow-hidden rounded-[28px] bg-white">
        {expanded ? (
          <div className="px-[14px] pb-[6px] pt-[14px]">{goToField('rail')}</div>
        ) : (
          <div className="flex justify-center pb-[6px] pt-[14px]">
            <button type="button" onClick={isTablet ? undefined : toggleCollapsed} title="GO TO" className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-[#F2F2F2] hover:bg-[#E5E5E5]"><SearchGlyph /></button>
          </div>
        )}
        <nav className="flex-1 overflow-y-auto px-[10px] pb-[10px]">
          {groups.map((g, gi) => (
            <div key={g.key}>
              {expanded ? (
                <div className="px-3 pb-[6px] pt-4 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-[#9CA3AF]">{g.label}</div>
              ) : gi > 0 ? (
                <div className="mx-[10px] my-[10px] h-px bg-[#F2F2F2]" />
              ) : null}
              {g.sections.map((s) => railItem(s, expanded ? 'rail' : 'collapsed'))}
            </div>
          ))}
          {!isTablet && (
            <button type="button" onClick={toggleCollapsed} className={`mt-[10px] h-8 rounded-full bg-[#F2F2F2] font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#6B7280] hover:text-black ${expanded ? 'px-[14px]' : 'w-full px-0'}`}>{expanded ? '← COLLAPSE' : '→'}</button>
          )}
        </nav>
        {footer(expanded ? 'rail' : 'collapsed')}
      </div>
    </aside>
  );

  const sectionBar = isMobile && !takeover && (
    <div className="sticky top-0 z-[35] bg-[#F5F5F5] px-3 py-[10px]">
      <button type="button" onClick={() => setSheetOpen((o) => !o)} className="flex h-[52px] w-full items-center gap-3 rounded-full bg-[#0A0A0A] py-0 pl-[6px] pr-[18px] text-white">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-extrabold" style={ringBorder('#0A0A0A')}>{section?.mark || '—'}</span>
        <span className="min-w-0 flex-1 truncate text-left font-mono text-[13px] font-bold uppercase tracking-[0.12em]">{section?.label || 'ADMIN'}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">{sheetOpen ? 'CLOSE' : 'SECTIONS'}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={sheetOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} /></svg>
      </button>
      {sheetOpen && (
        <div className="absolute left-3 right-3 top-[70px] z-40 max-h-[calc(100vh-90px)] overflow-y-auto rounded-[28px] bg-[#0A0A0A] p-3 text-white">
          {goToField('sheet')}
          {groups.map((g) => (
            <div key={g.key}>
              <div className="px-[14px] pb-[6px] pt-4 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[#737373]">{g.label}</div>
              {g.sections.map((s) => railItem(s, 'sheet'))}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <AdminShellContext.Provider value={ctx}>
      <div ref={measureRef} className="min-h-screen overflow-x-clip bg-[#F5F5F5] text-black">
        {navbar}
        {previewBanner}
        {sectionBar}
        <div className={`flex items-start ${isMobile ? 'flex-col' : ''}`}>
          {rail}
          {/* `w-full` on a phone: the row is a column there and `items-start` would otherwise
              size the main column to its widest child (a tab row, a chart) — every page then
              rendered wider than the screen and the right edge was cut off. */}
          <main className={`flex min-w-0 flex-1 flex-col gap-[14px] ${isMobile ? 'w-full' : ''} ${takeover ? 'p-0' : isMobile ? 'px-3 pb-14 pt-1' : 'p-4'}`}>
            {denied ? (
              lockCard(
                'NO LONGER YOURS.',
                section ? `${section.label} ISN'T ON YOUR KEY. ASK A SUPERADMIN IF YOU NEED IT.` : 'THERE IS NO SECTION HERE.',
                firstSection ? <Btn variant="white" className="self-center" onClick={() => navigate(`${base}/${firstSection.id === 'today' ? '' : firstSection.id}`)}>BACK TO {firstSection.label}</Btn> : null,
              )
            ) : (
              <Outlet />
            )}
          </main>
        </div>
        {personId && canSeePeople && <PersonDrawer userId={personId} onClose={closePerson} />}
        <Toast message={toastMsg} />
      </div>
    </AdminShellContext.Provider>
  );
}
